import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/api/axios';
export default function useInfiniteRecords<T extends { id: string }>(path: string, params: Record<string, unknown>, scope: unknown, enabled = true, size = 20) {
    const key = JSON.stringify([path, params, scope, enabled, size]);
    const [items, setItems] = useState<T[]>([]); const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false); const [total, setTotal] = useState(0); const [error, setError] = useState('');
    const [metadata, setMetadata] = useState<any>(null);
    const [revision, setRevision] = useState(0);
    const state = useRef({ generation: 0, next: 0, busy: false, more: false, controller: new AbortController() });
    const fetchPage = useCallback(async (first: boolean) => {
        const current = state.current;
        if (!enabled || (current.busy && !first) || (!first && !current.more)) return;
        if (first) { current.controller.abort(); current.controller = new AbortController(); current.generation++; current.next = 0; setItems([]); setTotal(0); }
        const generation = current.generation; const page = current.next;
        current.busy = true; setLoading(true); setError('');
        try {
            const response = await api.get(path, { params: { sort: 'id,asc', ...params, page, size }, signal: current.controller.signal });
            if (generation !== current.generation || current.controller.signal.aborted) return;
            const content: T[] = response.data.content || response.data.rows || []; const meta = response.data.page || response.data;
            setItems(old => { const merged = new Map((first ? [] : old).map(i => [i.id, i])); content.forEach(i => merged.set(i.id, i)); return [...merged.values()]; });
            if(first) setMetadata(response.data);
            current.next = page + 1; current.more = typeof response.data.hasMore === 'boolean' ? response.data.hasMore : meta.totalPages !== undefined ? current.next < meta.totalPages : !meta.last && content.length === size;
            setHasMore(current.more); setTotal(meta.totalElements ?? meta.totalRows ?? content.length);
        } catch (e: any) {
            if (generation === current.generation && e.code !== 'ERR_CANCELED') { current.more = false; setHasMore(false); setError(e.response?.data?.message || 'Não foi possível carregar os registros.'); }
        } finally { if (generation === current.generation) { current.busy = false; setLoading(false); } }
        // The serialized query is the dependency; inline parameter objects do not reset the list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    useEffect(() => {
        const current = state.current;
        current.controller.abort(); current.generation++; current.busy = false; current.more = false;
        setItems([]); setMetadata(null); setHasMore(false); setLoading(enabled); setError('');
        if (!enabled) return;
        const timer = setTimeout(() => fetchPage(true), 250);
        return () => { clearTimeout(timer); current.controller.abort(); current.generation++; };
    }, [fetchPage, revision, enabled]);
    const loadMore = useCallback(() => { void fetchPage(false); }, [fetchPage]);
    const reload = useCallback(() => setRevision(v => v + 1), []);
    return { items, metadata, loading, hasMore, total, error, loadMore, reload };
}
